<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import { toast } from "@/common/utils/toast";
import store from "@/store";

import {
  evaluationList,
  evaluationListByMember,
} from "@/common/api/rest/evaluation";

import type { EvaluationListType } from "@/common/types/evaluation";

import { PanelTheme } from "@/common/themes";

import {
  baseLoading,
  baseInput,
  baseSelect,
  cardEvaluation,
  svgIcon,
} from "@/common/components";

const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const listRef = ref();
const searchInput = ref("");

const evaluations = ref<EvaluationListType[]>([]);
const statusType = ref("PROGRESS");

const loading = ref(false);
const hasScroll = ref(false);

const accountId = computed(() => route.params.id as string);
const userId = computed(() => store.getters.user?.member_id as string);
const userType = computed(() => store.getters.type as string);

const statusOptions = computed(() => [
  {
    option: t("evaluation.labels.in_progress"),
    value: "PROGRESS",
  },
  {
    option: t("evaluation.labels.started"),
    value: "STARTED",
  },
  {
    option: t("evaluation.labels.pending"),
    value: "PENDING",
  },
  {
    option: t("evaluation.labels.concluded"),
    value: "CONCLUDED",
  },
  {
    option: t("evaluation.labels.canceled"),
    value: "CANCELED",
  },
]);

function checkScroll() {
  if (!listRef.value) return false;

  hasScroll.value = listRef.value?.scrollHeight > listRef.value?.clientHeight;
}

async function fetchEvaluationListAll() {
  loading.value = true;

  evaluationList(accountId.value, {
    page: 0,
    limit: 100,
    search: searchInput.value,
    full: true,
    status: statusType.value,
  })
    .then((response) => {
      const { success, data } = response;

      if (success) {
        evaluations.value = data.list;
      } else {
        toast({ message: data.message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      loading.value = false;
      checkScroll();
    });
}

async function fetchEvaluationListById() {
  loading.value = true;

  evaluationListByMember(accountId.value, userId.value, {
    page: 0,
    limit: 50,
    search: searchInput.value,
    all: true,
    status: statusType.value,
  })
    .then((response) => {
      const { success, data } = response;

      if (success) {
        evaluations.value = data.list;
      } else {
        toast({ message: data.message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      loading.value = false;
      checkScroll();
    });
}

function fetchData() {
  if (!accountId.value) return;

  switch (userType.value) {
    case "MANAGER":
    case "ADMIN":
      {
        fetchEvaluationListAll();
      }
      break;
    case "TECHNICAL":
    case "ASSISTANT":
      {
        fetchEvaluationListById();
      }
      break;
  }
}

function selectEvaluation(evaluation: EvaluationListType) {
  router.push(`/${accountId.value}/evaluate/${evaluation.evaluation_id}`);
}

function cleanSearch() {
  if (!searchInput.value) {
    fetchData();
  }
}

function searchValue() {
  fetchData();
}

function changeStatus() {
  localStorage.setItem("evaluation-status", statusType.value);

  fetchData();
}

watch(
  () => userType.value,
  () => {
    fetchData();
  }
);

onMounted(() => {
  const status = localStorage.getItem("evaluation-status");
  statusType.value = status || "PROGRESS";

  fetchData();
});
</script>

<template>
  <panel-theme>
    <section class="cockpit-page">
      <section class="header">
        <div class="actions">
          <base-select
            v-model="statusType"
            :value="statusType"
            :options="statusOptions"
            :is-empty="false"
            @change="changeStatus()"
          />
        </div>

        <div class="search">
          <base-input
            v-model="searchInput"
            :placeholder="t('common.labels.search')"
            background="#fff"
            color="#000"
            @update:model-value="cleanSearch()"
            @key:enter="searchValue()"
          >
            <template #sufix>
              <div class="icon">
                <svg-icon icon="search" size="1.75rem" @click="searchValue()" />
              </div>
            </template>
          </base-input>
        </div>
      </section>

      <section
        ref="listRef"
        class="evaluations"
        :class="{ scrollable: hasScroll }"
      >
        <div class="list">
          <template
            v-for="evaluation in evaluations"
            :key="evaluation.evaluation_id"
          >
            <card-evaluation
              :evaluation="evaluation"
              @click="selectEvaluation(evaluation)"
            />
          </template>
        </div>

        <template v-if="loading">
          <div class="flex items-center justify-center">
            <base-loading />
          </div>
        </template>

        <template v-if="!loading && evaluations.length === 0">
          <div class="empty">
            {{ t("evaluation.texts.empty_evaluation") }}
          </div>
        </template>
      </section>
    </section>
  </panel-theme>
</template>

<style lang="scss" scoped>
.cockpit-page {
  display: flex;
  flex-direction: column;
  gap: 1rem;

  .header {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    gap: 2rem;
    height: 2.9rem;

    .search {
      flex: 1;

      .icon {
        cursor: pointer;
        color: var(--text-off);

        &:hover {
          color: var(--primary);
        }
      }
    }

    .actions {
      display: flex;
      flex-direction: row;
      gap: 1rem;
      width: 12rem;
    }
  }

  .evaluations {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    height: calc(100dvh - 2.9rem - 1rem - 6rem);
    overflow: auto;

    &.scrollable {
      padding-inline-end: 0.5rem;
    }

    .list {
      display: flex;
      flex-direction: column;
      gap: 1rem;
      padding-block-end: 2rem;
    }

    .empty {
      padding-inline: 0.5rem;
    }
  }
}

@media screen and (max-width: 1024px) {
  .cockpit-page {
    .header {
      flex-direction: column;
      gap: 1rem;
      height: 7rem;

      .actions {
        width: 100%;

        button {
          font-size: 0.8rem;
          padding-inline: 0.5rem;
        }
      }
    }
    .evaluations {
      height: calc(100dvh - 7rem - 1rem - 6rem - 3.5rem);
    }
  }
}
</style>
